import { APIComponentAttributes, ArdoqId } from '@ardoq/api-types';
import {
  GridApi,
  IsServerSideGroupOpenByDefaultParams,
} from 'ag-grid-enterprise';
import { isEqual } from 'lodash';
import { InventoryContext } from '../types';
import { FilterModel } from '../filters/types';
import { inventoryHierarchyExpansionOperations } from './inventoryHierarchyExpansionOperations';

/**
 * Collapsed mode is recorded by the `filterSnapshotAtCollapseTime` and active
 * as long as filters do not change.
 * (UX: Currently, we want to switch into hierarchyExpanded when filters change.)
 */
const isHierarchyCollapsedMode = (
  hierarchy: InventoryContext['hierarchy'],
  currentFilterModel: FilterModel
): boolean => {
  if (!hierarchy.isHierarchyEnabled) return false;
  const nodeExpansion = hierarchy.nodeExpansion;
  if (!inventoryHierarchyExpansionOperations.isCollapsedAll(nodeExpansion)) {
    return false;
  }
  return isEqual(
    nodeExpansion.filterSnapshotAtCollapseTime,
    currentFilterModel
  );
};

const isServerSideGroup = (
  component: APIComponentAttributes,
  api: GridApi<APIComponentAttributes> | undefined
): boolean => {
  return getChildCount(component, api) > 0;
};

const hasFiltersApplied = (
  gridApi: GridApi<APIComponentAttributes>
): boolean => {
  const filterModel = gridApi.getFilterModel();
  return Boolean(filterModel && Object.keys(filterModel).length > 0);
};

const isServerSideGroupOpenByDefault = (
  params: IsServerSideGroupOpenByDefaultParams<
    APIComponentAttributes,
    InventoryContext
  >
) => {
  const hierarchy = params.context?.hierarchy;
  if (!hierarchy?.isHierarchyEnabled) return false;

  const nodeExpansion = hierarchy.nodeExpansion;
  if (inventoryHierarchyExpansionOperations.isExpandedAll(nodeExpansion)) {
    return true;
  }

  const filterModel = params.api.getFilterModel() as FilterModel;
  if (
    hasFiltersApplied(params.api) &&
    !isHierarchyCollapsedMode(hierarchy, filterModel)
  ) {
    return true;
  }
  if (
    !inventoryHierarchyExpansionOperations.isPartiallyExpanded(nodeExpansion)
  ) {
    return false;
  }
  return nodeExpansion.expandedNodeIds.has(params.rowNode.id ?? '');
};

const getChildCount = (
  component: APIComponentAttributes,
  api: GridApi<APIComponentAttributes> | undefined
): number => {
  if (!api) {
    return 0;
  }

  const context = api.getGridOption('context') as InventoryContext | undefined;
  if (!context || !context.hierarchy.isHierarchyEnabled) {
    return 0;
  }

  return (
    (component as APIComponentAttributes & { childCount?: number })
      .childCount ?? 0
  );
};

const getExpandedNodeIdsInGrid = (
  gridApi: GridApi<APIComponentAttributes>
): Set<ArdoqId> => {
  const expandedNodeIds = new Set<ArdoqId>();
  gridApi.forEachNode(node => {
    if (node.expanded && node.id) {
      expandedNodeIds.add(node.id);
    }
  });
  return expandedNodeIds;
};

const captureExpandedNodesForReexpansion = (
  gridApi: GridApi<APIComponentAttributes>
): void => {
  const context = gridApi.getGridOption('context') as
    | InventoryContext
    | undefined;
  if (!context?.hierarchy.isHierarchyEnabled) return;
  const hierarchy = context.hierarchy;
  if (
    inventoryHierarchyExpansionOperations.isExpandedAll(hierarchy.nodeExpansion)
  ) {
    return;
  }

  const expandedNodeIds = getExpandedNodeIdsInGrid(gridApi);
  if (
    inventoryHierarchyExpansionOperations.isCollapsedAll(
      hierarchy.nodeExpansion
    ) &&
    expandedNodeIds.size === 0
  ) {
    return;
  }
  hierarchy.nodeExpansion =
    inventoryHierarchyExpansionOperations.getPartiallyExpanded(expandedNodeIds);
};

/**
 * Leaves "Expand All" so newly loaded group nodes stop opening by default.
 * Called when the user collapses a node: from then on only the nodes they
 * actually have open are re-expanded after a refresh.
 */
const clearExpandAllMode = (gridApi: GridApi<APIComponentAttributes>): void => {
  const context = gridApi.getGridOption('context') as
    | InventoryContext
    | undefined;
  if (!context?.hierarchy.isHierarchyEnabled) return;
  const hierarchy = context.hierarchy;
  if (
    !inventoryHierarchyExpansionOperations.isExpandedAll(
      hierarchy.nodeExpansion
    )
  ) {
    return;
  }
  hierarchy.nodeExpansion =
    inventoryHierarchyExpansionOperations.getPartiallyExpanded(
      getExpandedNodeIdsInGrid(gridApi)
    );
};

/**
 * Marks a node to be expanded when AG Grid next loads it. Collapsed mode is
 * left, since a node to re-open means the tree is no longer all collapsed.
 */
const markNodeForReexpansion = (
  hierarchy: InventoryContext['hierarchy'],
  nodeId: ArdoqId
): void => {
  if (!hierarchy.isHierarchyEnabled) return;
  const nodeExpansion = hierarchy.nodeExpansion;
  if (inventoryHierarchyExpansionOperations.isExpandedAll(nodeExpansion))
    return;
  if (
    !inventoryHierarchyExpansionOperations.isPartiallyExpanded(nodeExpansion)
  ) {
    hierarchy.nodeExpansion =
      inventoryHierarchyExpansionOperations.getPartiallyExpanded(
        new Set([nodeId])
      );
    return;
  }
  nodeExpansion.expandedNodeIds.add(nodeId);
};

const clearCollapsedHierarchyModeIfFilterChanged = (
  hierarchy: InventoryContext['hierarchy'],
  currentFilterModel: FilterModel
): void => {
  if (!hierarchy.isHierarchyEnabled) return;
  const nodeExpansion = hierarchy.nodeExpansion;
  if (!inventoryHierarchyExpansionOperations.isCollapsedAll(nodeExpansion))
    return;
  if (
    !isEqual(nodeExpansion.filterSnapshotAtCollapseTime, currentFilterModel)
  ) {
    hierarchy.nodeExpansion =
      inventoryHierarchyExpansionOperations.getPartiallyExpanded();
  }
};

type LoadedBlockRoutes = {
  routesToRefreshInPlace: ArdoqId[][];
  routesToPurge: ArdoqId[][];
};

const toRouteKey = (route: ArdoqId[]): string => route.join('\0');

const getRoutesOfLoadedBlocks = (
  gridApi: GridApi<APIComponentAttributes>
): LoadedBlockRoutes => {
  const expandedRoutes: ArdoqId[][] = [[]];
  const expandedRouteKeys = new Set<string>([toRouteKey([])]);

  gridApi.forEachNode(node => {
    if (!node.expanded) return;
    const route = node.getRoute();
    if (!route) return;
    expandedRoutes.push(route);
    expandedRouteKeys.add(toRouteKey(route));
  });

  const isVisible = (route: ArdoqId[]): boolean =>
    route.every((_, index) =>
      expandedRouteKeys.has(toRouteKey(route.slice(0, index)))
    );

  const routesToRefreshInPlace = expandedRoutes.filter(isVisible);
  const visibleRouteKeys = new Set(routesToRefreshInPlace.map(toRouteKey));

  const routesToPurge = gridApi
    .getServerSideGroupLevelState()
    .map(({ route }) => route)
    .filter(route => !visibleRouteKeys.has(toRouteKey(route)));

  return { routesToRefreshInPlace, routesToPurge };
};

export const hierarchyInAgGridOperations = {
  getChildCount,
  hasFiltersApplied,
  isServerSideGroup,
  isServerSideGroupOpenByDefault,
  isHierarchyCollapsedMode,
  clearExpandAllMode,
  markNodeForReexpansion,
  captureExpandedNodesForReexpansion,
  clearCollapsedHierarchyModeIfFilterChanged,
  getRoutesOfLoadedBlocks,
};
